// Lume (contracts/lume.md): the line library (pushes and in-app lines), Ask Lume usage, and the engagement
// report. Lines: every role reads, editors write. Ask Lume usage: admin. Engagement: every role. The lume.*
// settings go through GET/PUT /config (useConfig, useSetConfig). Kept apart from client.ts / queries.ts.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";
import { LOCALES } from "./messaging";

const count = z.number().int().min(0);
const rate = z.number().min(0).nullable();

// ---------------------------------------------------------------- lines

export const LineText = z.object({ title: z.string().optional(), text: z.string().optional() });
/** Per locale `{ title? ≤ 50 (pushes), text ≤ 160 }`; `en` required, the rest fall back per field. */
export const LineContent = z.partialRecord(z.enum(LOCALES), LineText);
export type LineContent = z.infer<typeof LineContent>;

export const LumeLine = z.object({
  id: z.string(),
  channel: z.enum(["push", "app"]),
  trigger: z.string(),
  content: LineContent,
  mood: z.string().nullable(),
  weight: z.number().int(),
  active: z.boolean(),
  /** Push lines: players who got it in the last 30 days. */
  sends30d: count,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LumeLine = z.infer<typeof LumeLine>;
export const TriggerSpec = z.object({ trigger: z.string(), channel: z.enum(["push", "app"]), description: z.string(), placeholders: z.array(z.string()) });
export type TriggerSpec = z.infer<typeof TriggerSpec>;
export const LumeLines = z.object({ items: z.array(LumeLine), triggers: z.array(TriggerSpec) });

export interface LineDraft {
  channel: "push" | "app";
  trigger: string;
  content: LineContent;
  mood: string | null;
  weight: number;
  active: boolean;
}

// ---------------------------------------------------------------- Ask Lume

export const CHAT_STATUSES = ["answered", "refused", "failed"] as const;
export type ChatStatus = (typeof CHAT_STATUSES)[number];
export const ChatDay = z.object({ date: z.string(), questions: count, players: count, costUsd: z.number().min(0), refused: count, failed: count });
export type ChatDay = z.infer<typeof ChatDay>;
export const ChatTurn = z.object({ at: z.string(), userId: z.string(), handle: z.string().nullable(), question: z.string(), answer: z.string().nullable(), status: z.enum(CHAT_STATUSES) });
export type ChatTurn = z.infer<typeof ChatTurn>;
/** `days` only lists dates with turns; `recent` the latest 100 turns. */
export const ChatStats = z.object({ days: z.array(ChatDay), recent: z.array(ChatTurn) });

// ---------------------------------------------------------------- engagement

const PushCounts = z.object({ sent: count, opened: count });
export const EngagementDay = z.object({
  stories: z.object({ views: count, completed: count }),
  brain: z.object({ opens: count }),
  banners: z.object({ shown: count, tapped: count, dismissed: count }),
  announcements: z.object({ reads: count, taps: count }),
  pushes: z.object({ sent: count, opened: count, byKind: z.record(z.string(), PushCounts) }),
  lume: z.object({ questions: count, players: count }),
  focus: z.object({ attempts: count, players: count }),
  review: z.object({ cleared: count }),
  active: count,
});
export type EngagementDay = z.infer<typeof EngagementDay>;
export const Engagement = z.object({
  from: z.string(),
  to: z.string(),
  days: z.array(EngagementDay.extend({ date: z.string() })),
  /** Sums over the range (`active`: the busiest day). */
  totals: EngagementDay,
  top: z.object({
    stories: z.array(z.object({ id: z.string(), title: z.string().nullable(), views: count, completed: count, completionRate: rate })),
    brainCards: z.array(z.object({ id: z.string(), title: z.string().nullable(), opens: count })),
    banners: z.array(z.object({ id: z.string(), placement: z.string(), title: z.string().nullable(), shown: count, tapped: count, ctr: rate })),
    announcements: z.array(z.object({ id: z.string(), title: z.string(), reads: count, taps: count, tapRate: rate })),
  }),
  /** Players who used the app within 24 h of a push, by push kind. */
  pushReturn: z.array(z.object({ kind: z.string(), players: count, returned: count, rate })),
});
export type Engagement = z.infer<typeof Engagement>;

// ---------------------------------------------------------------- client and hooks

export interface DateRange {
  from: string;
  to: string;
}

export const lumeApi = {
  lines: (signal?: AbortSignal) => request(LumeLines, "GET", "/lume/lines", { signal }),
  create: (draft: LineDraft) => request(LumeLine, "POST", "/lume/lines", { body: draft }),
  update: (id: string, patch: Partial<LineDraft>) => request(LumeLine, "PATCH", `/lume/lines/${encodeURIComponent(id)}`, { body: patch }),
  remove: (id: string) => request(z.null(), "DELETE", `/lume/lines/${encodeURIComponent(id)}`),
  chat: (range: DateRange, signal?: AbortSignal) => request(ChatStats, "GET", "/lume/chat", { query: { ...range }, signal }),
  engagement: (range: DateRange, signal?: AbortSignal) => request(Engagement, "GET", "/engagement", { query: { ...range }, signal }),
};

export const lumeKeys = {
  lines: ["lume", "lines"] as const,
  chat: (r: DateRange) => ["lume", "chat", r.from, r.to] as const,
  engagement: (r: DateRange) => ["engagement", r.from, r.to] as const,
};

export const useLumeLines = () => useQuery({ queryKey: lumeKeys.lines, queryFn: ({ signal }) => lumeApi.lines(signal) });
export const useChatStats = (range: DateRange, enabled: boolean) =>
  useQuery({ queryKey: lumeKeys.chat(range), queryFn: ({ signal }) => lumeApi.chat(range, signal), enabled, placeholderData: keepPreviousData });
export const useEngagement = (range: DateRange) =>
  useQuery({ queryKey: lumeKeys.engagement(range), queryFn: ({ signal }) => lumeApi.engagement(range, signal), placeholderData: keepPreviousData });

function useLineMutation<V, R>(fn: (v: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: lumeKeys.lines });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useCreateLine = () => useLineMutation((draft: LineDraft) => lumeApi.create(draft));
export const useUpdateLine = () => useLineMutation(({ id, patch }: { id: string; patch: Partial<LineDraft> }) => lumeApi.update(id, patch));
export const useDeleteLine = () => useLineMutation((id: string) => lumeApi.remove(id));
/** Several lines at once (bulk activate / deactivate / delete): one request each, then one refresh. */
export const useBulkLines = () =>
  useLineMutation(async ({ ids, op }: { ids: string[]; op: "activate" | "deactivate" | "delete" }) => {
    for (const id of ids) {
      if (op === "delete") await lumeApi.remove(id);
      else await lumeApi.update(id, { active: op === "activate" });
    }
    return ids.length;
  });
